<template>
  <div class="upgrade-stepper-component">
    <KmModal v-model="model" maxWidth="700" persistent noClickAnimation contentClass="payment-modal">
      <template slot="title">
        <VScrollXReverseTransition mode="out-in">
          <div :key="currentStep">
            <template v-if="currentStep === STEP_FROM_PLUS_PLAN_WARNING">
              <span class="warning--text"> Warning </span>
            </template>
            <template v-else-if="currentStep === STEP_CHURN"> Your feedback on PM2 Plus </template>
            <template v-else-if="currentStep === STEP_CARD"> Add a card </template>
            <template v-else-if="currentStep === STEP_FINAL"> Confirmation </template>
          </div>
        </VScrollXReverseTransition>
      </template>

      <template v-if="hasInfos === true">
        <VScrollXReverseTransition mode="out-in">
          <div :key="currentStep" class="pa-3">
            <template v-if="currentStep === STEP_FROM_PLUS_PLAN_WARNING">
              <p>
                You are currently subscribed to a legacy plan. You will lose a few features by
                switching to this new plan:
              </p>
              <ul class="feature-list">
                <li>Profiling</li>
                <li>Transaction Tracing</li>
              </ul>

              <div class="flex-row">
                <VSpacer />
                <VBtn class="contrast warning--text ma-0" @click="goToNextStep"> Continue </VBtn>
              </div>
            </template>
            <template v-else-if="currentStep === STEP_CHURN">
              <ChurnForm @submit="onChurnSubmit" />
            </template>
            <template v-else-if="currentStep === STEP_CARD">
              <AddCardForm inUpgradeProcess :handleAddCard="addCard" :handleBack="goToPreviousStep" />
            </template>
            <template v-else-if="currentStep === STEP_FINAL">
              <template v-if="!upgraded">
                <template v-if="!isTrial && plan.is_premium && iHaveACoupon">
                  <VTextField
                    v-model="couponText"
                    name="coupon"
                    label="Coupon Code"
                    :errorMessages="validCoupon"
                  />
                  <h2 v-if="coupon.id" class="subheading">Coupon applied</h2>
                </template>

                <BillingCoupon
                  :plan="plan"
                  :isTrial="isTrial"
                  :coupon="coupon"
                  :loading="purchaseLoading"
                  @close="closePopup"
                  @submit="purchase"
                />
              </template>
              <template v-else>
                <h2 v-if="isPremiumUpgrade">
                  Congratulation!<br />
                  You now benefit from a premium PM2 subscription.<br /><br /><br />
                </h2>

                <h2 v-else-if="isPremiumDowngrade">Your subscription has been cancelled.</h2>

                <h2 v-else>Your bucket has been {{ isDowngrade ? 'downgraded' : 'upgraded' }}.</h2>

                <h2 v-if="isPremiumUpgrade || isPremiumDowngrade">
                  Your bucket is being moved to an other infrastructure. It might take up to 1
                  minute to get data from your app & servers.
                </h2>
                <VBtn class="button-reload" color="primary" @click="handleBack"> Next </VBtn>
              </template>
            </template>
          </div>
        </VScrollXReverseTransition>
      </template>
      <div v-else class="loader pa-4">
        <VProgressCircular :size="50" color="primary" indeterminate />
      </div>
    </KmModal>
  </div>
</template>

<script>
import * as act from '@/store/actions'
import { debounce } from 'lodash'

import stripe from '@/utils/stripe'
import AddCardForm from '@/components/pricing/AddCardForm'
import BillingCoupon from './BillingCoupon'
import ChurnForm from './ChurnForm'
import KmModal from '@/components/KmModal'
import {
  trackPurchase,
  trackCheckoutAddCardViewed,
  trackSubscriptionCancelled,
  trackSubscriptionDowngrade,
  trackSubscriptionUpgraded,
} from '@/tracking'

const STEP_FROM_PLUS_PLAN_WARNING = 1
const STEP_CHURN = 2
const STEP_CARD = 3
const STEP_FINAL = 4

export default {
  name: 'UpgradeStepper',
  components: {
    AddCardForm,
    ChurnForm,
    BillingCoupon,
    KmModal,
  },
  props: {
    plan: {
      name: 'plan',
      type: Object,
    },
    isTrial: {
      type: Boolean,
    },
    handleBack: {
      name: 'handleBack',
      type: Function,
    },
    isOpen: {
      name: 'isOpen',
      type: Boolean,
    },
  },
  data() {
    return {
      currentStepIndex: 0,
      iHaveACoupon: false,
      coupon: {},
      validCoupon: [],
      couponText: '',
      upgraded: false,
      purchaseLoading: false,
      steps: [],
      hasInfos: false,

      STEP_FROM_PLUS_PLAN_WARNING,
      STEP_CHURN,
      STEP_CARD,
      STEP_FINAL,
    }
  },

  computed: {
    cards() {
      return this.$store.getters.billing({}).cards
    },
    model: {
      get() {
        return this.isOpen
      },
      set(value) {
        if (!value) this.$emit('close')
      },
    },
    plans: {
      get() {
        return this.$store.getters.plans
      },
    },
    currentPlan() {
      return this.$store.getters.currentPlan
    },
    isPremiumUpgrade() {
      return !this.currentPlan.is_premium && this.plan.is_premium
    },
    isPremiumDowngrade() {
      return this.currentPlan.is_premium && !this.plan.is_premium
    },
    isDowngrade() {
      return (
        this.currentPlan.process_limit > this.plan.process_limit ||
        (this.currentPlan.is_premium && !this.plan.is_premium)
      )
    },
    currentStep() {
      return this.steps[this.currentStepIndex]
    },
  },

  watch: {
    couponText: debounce(function (coupon) {
      if (coupon === '') {
        this.coupon = {}
        this.validCoupon = []
        return
      }
      this.$store
        .dispatch(act.RETRIEVE_COUPON, { coupon })
        .then((res) => {
          this.coupon = res.data.coupon
          this.validCoupon = []
        })
        .catch(() => {
          this.coupon = {}
          this.validCoupon = 'Coupon not valid'
        })
    }, 500),
    model(value) {
      if (value) {
        this.currentStepIndex = 0
        this.feedback = false

        const steps = []
        if (this.plan) {
          if (this.plan.name.startsWith('plus_') && this.currentPlan.name.startsWith('pro_')) {
            steps.push(STEP_FROM_PLUS_PLAN_WARNING)
          }
          if (this.plan.amount === 0) {
            // this is a downgrade
            steps.push(STEP_CHURN)
          } else if (!this.isTrial && this.plan.is_premium && !this.cards.length) {
            steps.push(STEP_CARD)
          }
          steps.push(STEP_FINAL)
        }
        this.steps = steps
      } else {
        this.currentStepIndex = 0
      }
    },
    currentStep(newStep) {
      if (newStep === STEP_CARD) {
        trackCheckoutAddCardViewed({
          cartNumberProcess: this.plan.process_limit,
          cartPlanDuration: this.plan.interval,
        })
      }
    },
  },

  mounted() {
    if (this.cards.length !== 0) {
      this.hasInfos = true
      return
    }
    this.$store.getters.currentBillingCards.finally(() => {
      this.hasInfos = true
    })
  },
  methods: {
    closePopup() {
      this.$emit('close')
    },
    async addCard({ card, vatNumber, companyName }) {
      if (vatNumber || companyName) {
        // Update billing metadata if needed
        const currentMeta = this.$store.getters.billingMetadata({})
        await this.$store.dispatch(act.UPDATE_BILLING_META, {
          vatNumber: vatNumber || currentMeta.vat_number,
          companyName: companyName || currentMeta.company_name,
          receiptEmail: currentMeta.receipt_email,
        })
      }

      await this.$store.dispatch(act.ADD_BILLING_CARD, { token: card.id })
      this.goToNextStep()
    },
    onChurnSubmit(churn) {
      this.feedback = churn
      this.goToNextStep()
    },
    goToNextStep() {
      this.currentStepIndex += 1
    },
    goToPreviousStep() {
      if (this.currentStepIndex > 0) {
        this.currentStepIndex -= 1
      } else {
        // Back from the first step: let the parent bring the plan choice back
        this.$emit('back')
        this.closePopup()
      }
    },
    async purchase() {
      this.upgraded = false
      this.purchaseLoading = true

      const previousPlanWasPremium = this.currentPlan.is_premium
      const previousPlanName = this.currentPlan.name
      const previousPlanProcessLimit = this.currentPlan.process_limit

      let errorMessage = false
      let paymentIntent = null
      if (this.isTrial) {
        try {
          await this.$store.dispatch(act.START_TRIAL, {
            plan: this.plan.id,
          })
        } catch (e) {
          errorMessage = 'Subscription failed'
        }
      } else {
        try {
          const result = await this.$store.dispatch(act.SUBSCRIBE_PLAN, {
            plan: this.plan.id,
            coupon: this.coupon.id,
          })
          paymentIntent = result.paymentIntent

          if (paymentIntent && paymentIntent.status === 'requires_action') {
            const handleCardPaymentResult = await stripe().handleCardPayment(
              paymentIntent.client_secret,
            )

            if (handleCardPaymentResult.error) {
              errorMessage =
                handleCardPaymentResult.error.message ||
                'Payment failed. You have not been charged.'
            } else {
              await this.$store.dispatch(act.PAYMENT_INTENT_SUCCEED, {
                paymentIntent: paymentIntent.id,
              })
            }
          }
        } catch (e) {
          errorMessage = e.message || 'Payment failed. You have not been charged.'
        }
      }

      if (errorMessage) {
        this.purchaseLoading = false
        this.$store.dispatch(act.DISPLAY_NOTIFICATION, {
          type: 'error',
          message: errorMessage,
        })
      } else {
        this.upgraded = true
        this.purchaseLoading = false

        const isDowngrade = previousPlanProcessLimit > this.plan.process_limit ||
          (previousPlanWasPremium && !this.plan.is_premium)
        const isPremiumDowngrade = previousPlanWasPremium && !this.plan.is_premium

        if (isDowngrade && previousPlanWasPremium && this.plan.is_premium) {
          trackSubscriptionDowngrade({
            planNameCurrent: this.plan.name,
            planNamePrevious: previousPlanName,
            cancelReason: this.feedback ? this.feedback.reasonText : undefined,
          })
        } else if (isPremiumDowngrade) {
          trackSubscriptionCancelled({
            planName: previousPlanName,
            cancelReason: this.feedback ? this.feedback.reasonText : undefined,
          })
        } else if (previousPlanWasPremium && this.plan.is_premium) {
          trackSubscriptionUpgraded({
            planNameCurrent: this.plan.name,
            planNamePrevious: previousPlanName,
            revenue: this.plan.amount / 100,
            interval: this.plan.interval,
          })
        } else if (this.plan.is_premium) {
          trackPurchase({
            planName: this.plan.name,
            planId: this.plan.id,
            revenue: this.plan.amount / 100,
            isTrial: this.isTrial,
            interval: this.plan.interval,
            subscriptionId: paymentIntent ? paymentIntent.id : undefined,
          })
        }

        if (this.feedback) {
          // No need to await for this
          this.$store
            .dispatch(act.SEND_CHURN_FEEDBACK, { feedback: this.feedback })
            // If the feedback action fails, just log the error
            .catch((e) => console.error(e))
        }
      }
    },
  },
}
</script>
<style scoped>
.button-reload {
  display: block;
  margin-left: auto;
}
.loader {
  display: flex;
  justify-content: center;
}
.feature-list li {
  list-style-type: circle;
  margin-left: 30px;
}
</style>
